import { create } from 'zustand';
import {
  filterByDateRange,
  filterByLangValue,
  filterByName,
  filterByRange,
  sortByLangValue,
  sortByName,
} from '../features/table/helpers';
import { PaginationType } from '../types/pagination';
import { SortType } from '../types/props';
import {
  BranchType,
  deleteBranch,
  fetchBranches,
  FetchBranchesRequest,
  postBranch,
  putBranch,
  restoreBranch,
} from './api/branches-api';
import { useAlertStore } from './use-alert-store';

interface Store extends Partial<PaginationType> {
  place?: BranchType[];
  fetchBranches: (req: FetchBranchesRequest) => Promise<void>;
  postBranch: (data: Partial<BranchType>) => Promise<void>;
  putBranch: (data: Partial<BranchType>) => Promise<void>;
  archiveBranch: (id: string) => Promise<void>;
  restoreBranch: (id: string) => Promise<void>;
  isLoading?: boolean;
  isLoadingFetch?: boolean;
}

export const useBranchesStore = create<Store>((set) => ({
  fetchBranches: async (req) => {
    set({ isLoadingFetch: true });
    try {
      const { place, ...other } = await fetchBranches(req);
      let branches = [...place];

      if (req.name) {
        branches = branches.filter((item) => filterByLangValue(item.name, req.name));
      }

      if (req.description) {
        branches = branches.filter((item) => filterByLangValue(item.description, req.description));
      }

      if (req.zip) {
        branches = branches.filter((item) => filterByName(item.zip, req.zip));
      }

      if (req.address) {
        branches = branches.filter((item) => filterByLangValue(item.address, req.address));
      }

      if (req.phone) {
        branches = branches.filter((item) => filterByName(item.phone, req.phone));
      }

      if (req.created_at) {
        branches = filterByDateRange(branches, req.created_at, 'created_at');
      }

      if (req.staff_count) {
        branches = branches.filter((item) =>
          filterByRange(Number(item.staff_count), String(req.staff_count)),
        );
      }

      if (req.field === 'staff_count') {
        branches = branches.sort((a, b) =>
          sortByName(String(a.staff_count), String(b.staff_count), req.dir as SortType),
        );
      }

      if (req.field === 'name') {
        branches = branches.sort((a, b) => sortByLangValue(a.name, b.name, req.dir as SortType));
      }

      if (req.field === 'description') {
        branches = branches.sort((a, b) =>
          sortByLangValue(a.description, b.description, req.dir as SortType),
        );
      }

      if (req.field === 'zip') {
        branches = branches.sort((a, b) => sortByName(a.zip, b.zip, req.dir as SortType));
      }

      if (req.field === 'address') {
        branches = branches.sort((a, b) =>
          sortByLangValue(a.address, b.address, req.dir as SortType),
        );
      }

      if (req.field === 'phone') {
        branches = branches.sort((a, b) => sortByName(a.phone, b.phone, req.dir as SortType));
      }

      if (req.field === 'created_at') {
        branches = branches.sort((a, b) =>
          sortByName(a.created_at, b.created_at, req.dir as SortType),
        );
      }

      set({ ...other, place: branches });
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось получить данные по филиалам', variant: 'danger' });
    } finally {
      set({ isLoadingFetch: false });
    }
  },
  postBranch: async (req) => {
    set({ isLoading: true });
    try {
      const resp = await postBranch(req);
      set((store) => ({ ...store, place: [resp, ...(store.place || [])] }));
      useAlertStore.getState().add({ label: 'Филиал успешно создан', variant: 'success' });
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось сохранить данные по филиалу', variant: 'danger' });
    } finally {
      set({ isLoading: false });
    }
  },
  putBranch: async (req) => {
    set({ isLoading: true });
    try {
      const resp = await putBranch(req);
      set((store) => ({
        ...store,
        place: store.place?.map((item) => (item.id === resp.id ? resp : item)),
      }));
      useAlertStore.getState().add({ label: 'Филиал успешно изменен', variant: 'success' });
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось обновить данные по филиалу', variant: 'danger' });
    } finally {
      set({ isLoading: false });
    }
  },
  archiveBranch: async (id) => {
    set({ isLoading: true });
    try {
      await deleteBranch(id);
      set((store) => ({
        ...store,
        place: store.place?.map((item) =>
          String(item.id) === id ? { ...item, is_deleted: true } : item,
        ),
      }));
      useAlertStore.getState().add({ label: 'Филиал перемещен в архив', variant: 'success' });
    } catch (e) {
      console.error(e);
      useAlertStore.getState().add({ label: 'Не удалось архивировать филиал', variant: 'danger' });
    } finally {
      set({ isLoading: false });
    }
  },
  restoreBranch: async (id) => {
    set({ isLoading: true });
    try {
      await restoreBranch(id);
      set((store) => ({
        ...store,
        place: store.place?.map((item) =>
          String(item.id) === id ? { ...item, is_deleted: false } : item,
        ),
      }));
      useAlertStore.getState().add({ label: 'Филиал восстановлен', variant: 'success' });
    } catch (e) {
      console.error(e);
      useAlertStore.getState().add({ label: 'Не удалось восстановить филиал', variant: 'danger' });
    } finally {
      set({ isLoading: false });
    }
  },
}));
